import { defineStore } from 'pinia'
import { ISensor } from '@/interfaces/entities/sensor'
import { IAnomalyDetectorTotalizer, IFieldStatisticsCacheEntry } from '@/interfaces/stores/anomalyDetector'
import { IStatsSensor } from '@/interfaces/entities/sensor'
import { IAnomalyDetectorPages } from '@/interfaces/components/dataTableAnomalyDetector/anomalyDetector-pages'
import { IFieldStatistic, parseFieldStatisticsResponse } from '@/interfaces/api/fieldStatistics'
import { useLoadingStore } from './loading'
import { useErrorStore } from './error'
import AnomalyDetectionResource from '@/api/resources/anomaly_detection'
import { periodFromAnomalyWeekSunday } from '@/utils/date/date-time'

const FIELD_STATISTICS_CACHE_TTL_MS = 5 * 60 * 1000

export const useAnomalyDetectorStore = defineStore({
    id: 'anomalyDetector',
    persist: {
        paths: [
            'currentSensorFilter',
            'currentVariabilityLevelFilter',
            'currentWeekFilter',
            'currentPeriodFilter',
            'tableItemsPerPage',
        ],
    },

    state: () => ({
        totalizers: {} as IAnomalyDetectorTotalizer,
        criticalSensors: [] as IStatsSensor[],
        attentionSensors: [] as IStatsSensor[],
        anomalyDetectorsData: [] as IStatsSensor[],
        analysis_date: null as Date | null,
        currentSensorFilter: null as ISensor | null,
        currentVariabilityLevelFilter: '' as string | null,
        currentWeekFilter: null as Date | null,
        /** [início segunda, fim domingo] no fuso local; derivado de currentWeekFilter (domingo da semana). */
        currentPeriodFilter: null as [Date, Date] | null,
        pagination: null as IAnomalyDetectorPages['pagy'] | null,
        hasAppliedSearch: false,
        tablePage: 1,
        tableItemsPerPage: 10,
        fieldStatistics: {} as Record<string, IFieldStatistic | null>,
        fieldStatisticsLoading: false,
        fieldStatisticsCache: {} as Record<string, IFieldStatisticsCacheEntry>,
    }),

    getters: {
        getTotalizers: (state) => state.totalizers,
        getCriticalSensors: (state) => state.criticalSensors,
        getAttentionSensors: (state) => state.attentionSensors,
        getAnomalyDetectorsData: (state) => state.anomalyDetectorsData,
        getAnalysisDate: (state) => state.analysis_date,
        getCurrentSensorFilter: (state) => state.currentSensorFilter,
        getCurrentVariabilityLevelFilter: (state) => state.currentVariabilityLevelFilter,
        getCurrentWeekFilter: (state) => state.currentWeekFilter,
        getCurrentPeriodFilter: (state): [Date, Date] | null => {
            const fromWeek = periodFromAnomalyWeekSunday(state.currentWeekFilter)
            if (fromWeek) return fromWeek
            if (
                state.currentPeriodFilter != null &&
                Array.isArray(state.currentPeriodFilter) &&
                state.currentPeriodFilter.length === 2
            ) {
                const [a, b] = state.currentPeriodFilter
                const start = new Date(a as Date | string)
                const end = new Date(b as Date | string)
                if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
                    return [start, end]
                }
            }
            return null
        },
        getPagination: (state) => state.pagination,
        getHasAppliedSearch: (state) => state.hasAppliedSearch,
        getTablePage: (state) => state.tablePage,
        getTableItemsPerPage: (state) => state.tableItemsPerPage,
        getFieldStatistics: (state) => state.fieldStatistics,
        isFieldStatisticsLoading: (state) => state.fieldStatisticsLoading,
    },

    actions: {
        async getAnomalyDetectorPages(page: number, items: number) {
            const loadingStore = useLoadingStore()
            const sensor_id = this.currentSensorFilter?.id?.toString() ?? ''
            const variability_level = this.currentVariabilityLevelFilter ?? ''
            
            let analysis_date = ''
            if (this.currentWeekFilter) {
                const date = new Date(this.currentWeekFilter)
                date.setHours(0, 0, 0, 0)
                // Format YYYY-MM-DD
                analysis_date = date.toISOString().split('T')[0]
            }

            try {
                loadingStore.initLoading()

                const response = await AnomalyDetectionResource.getAnomalyDetectionPages(
                    page,
                    items,
                    sensor_id,
                    variability_level,
                    analysis_date,
                )

                const data = response['data']
                const totalizers = response['totalizers']

                this.anomalyDetectorsData = data || []
                this.criticalSensors = totalizers.critical_sensors || []
                this.attentionSensors = totalizers.attention_sensors || []
                
                // Update totalizers state to match backend
                this.totalizers = {
                    total_sensors: totalizers.total_sensors,
                    health_score: totalizers.percentage_normal,
                    critical_sensors: totalizers.critical_sensors,
                    attention_sensors: totalizers.attention_sensors,
                    week: this.currentWeekFilter
                }
                
                this.pagination = response['pagy'] || {
                    count: data.length,
                    prev: page > 1 ? page - 1 : null,
                    next: data.length === items ? page + 1 : null,
                    pages: Math.ceil(data.length / items) || 1,
                }

                this.tablePage = page
                this.tableItemsPerPage = items
                this.hasAppliedSearch = true
            } catch (error: unknown) {
                const err = error as { response?: { data?: { errors?: string[] } } }
                useErrorStore().setErrors('Falha ao buscar dados da Anomalia Detector!', [err?.response?.data?.errors?.[0] || 'Falha ao buscar dados da Anomalia Detector'])
            } finally {
                loadingStore.removeLoading()
            }
        },

        resetSearchResults() {
            this.hasAppliedSearch = false
            this.anomalyDetectorsData = []
            this.criticalSensors = []
            this.attentionSensors = []
            this.totalizers = {} as IAnomalyDetectorTotalizer
            this.pagination = null
            this.tablePage = 1
        },

        setCurrentSensorFilter(sensor: ISensor | null) {
            this.currentSensorFilter = sensor
        },

        setCurrentVariabilityLevelFilter(variability_level: string | null) {
            this.currentVariabilityLevelFilter = variability_level
        },

        setCurrentWeekFilter(week: Date | null) {
            this.currentWeekFilter = week
            this.currentPeriodFilter = periodFromAnomalyWeekSunday(week)
            this.clearFieldStatisticsCache()
        },

        clearFieldStatisticsCache() {
            this.fieldStatisticsCache = {}
        },

        async fetchFieldStatistics(sensorId: number, fields: string[], startDate: string, endDate: string) {
            const cacheKey = `${sensorId}_${startDate}_${endDate}`
            const cached = this.fieldStatisticsCache[cacheKey]

            if (cached && Date.now() - cached.timestamp < FIELD_STATISTICS_CACHE_TTL_MS) {
                this.fieldStatistics = { ...cached.data }
                return
            }

            this.fieldStatistics = {}
            this.fieldStatisticsLoading = true
            try {
                const results = await Promise.all(
                    fields.map((field) => AnomalyDetectionResource.getFieldStatistics(sensorId, field, startDate, endDate)),
                )

                const next: Record<string, IFieldStatistic | null> = {}
                fields.forEach((field, index) => {
                    next[field] = parseFieldStatisticsResponse(results[index])
                })
                this.fieldStatistics = next

                this.fieldStatisticsCache[cacheKey] = {
                    data: { ...next },
                    timestamp: Date.now(),
                    sensorId,
                    startDate,
                    endDate,
                }
            } catch (error: unknown) {
                const err = error as { response?: { data?: { errors?: string[] } } }
                useErrorStore().setErrors('Falha ao buscar estatísticas do campo', [
                    err?.response?.data?.errors?.[0] || 'Falha ao buscar estatísticas do campo',
                ])
            } finally {
                this.fieldStatisticsLoading = false
            }
        },

    },
    
})
